// Auto re-issue: keeps one issue's alerts live by re-issuing them on a
// schedule (reissue.js) until it is switched off, a re-issue fails, or the
// page goes away. Plain JS with injectable timers so it is tested on its own;
// App.jsx only wires it to buttons.

import { nextReissueDelayMs } from "./reissue.js";

const BUSY_RETRY_MS = 1000;

/**
 * reissue(issueId) → Promise<{ ok, anchorMs?, error? }>, or
 *   { ok: false, retryInMs, waitingFor } when nothing has started yet (wait, don't stop);
 * onChange({ target, anchorMs, stopped }) after every change;
 * isBusy() true while another issue is in flight (the timer waits).
 */
export function createAutoReissuer({
  reissue,
  onChange = () => {},
  isBusy = () => false,
  now = () => Date.now(),
  setTimer = (f, ms) => setTimeout(f, ms),
  clearTimer = (t) => clearTimeout(t),
}) {
  let target = null;
  let anchorMs = null;
  let timer = null;
  let stopped = null;
  let waitingFor = null;
  let generation = 0;

  const snapshot = () => ({ target, anchorMs, stopped, waitingFor });
  const emit = () => onChange(snapshot());
  const cancel = () => {
    if (timer != null) clearTimer(timer);
    timer = null;
  };

  const schedule = (delayMs) => {
    cancel();
    if (!target) return;
    const gen = generation;
    timer = setTimer(() => fire(gen), delayMs);
  };

  async function fire(gen) {
    timer = null;
    if (!target || gen !== generation) return;
    if (isBusy()) {
      schedule(BUSY_RETRY_MS);
      return;
    }
    const id = target.issueId;
    let res;
    try {
      res = await reissue(id);
    } catch (e) {
      res = { ok: false, error: e?.message || String(e) };
    }
    if (!target || gen !== generation || target.issueId !== id) return;
    if (!res?.ok && Number.isFinite(res?.retryInMs)) {
      // Nothing has started yet: come back when the first scenario does.
      waitingFor = res.waitingFor || "the first onset";
      schedule(res.retryInMs);
      emit();
      return;
    }
    if (!res?.ok) {
      stop(`re-issue failed: ${res?.error || "unknown error"}`);
      return;
    }
    waitingFor = null;
    anchorMs = Number.isFinite(res.anchorMs) ? res.anchorMs : now();
    schedule(nextReissueDelayMs(anchorMs, now(), target.minutes));
    emit();
  }

  /** Keep `t` = { issueId, name, minutes } alive; anchor = when its alerts
   * were last sent (browser clock), or null to re-issue at once. */
  function start(t, anchor = null) {
    generation += 1;
    target = t;
    anchorMs = anchor;
    stopped = null;
    waitingFor = null;
    schedule(nextReissueDelayMs(anchor, now(), t.minutes));
    emit();
  }

  function stop(reason = null) {
    generation += 1;
    cancel();
    const was = target;
    target = null;
    waitingFor = null;
    stopped = was ? reason : stopped;
    emit();
  }

  /** A re-issue made by hand: when it is the target, count from it. */
  function note(issueId, anchor) {
    if (!target || target.issueId !== issueId) return;
    anchorMs = anchor;
    schedule(nextReissueDelayMs(anchor, now(), target.minutes));
    emit();
  }

  function bindPagehide(win) {
    const off = () => stop("the page was closed");
    win.addEventListener("pagehide", off);
    return () => win.removeEventListener("pagehide", off);
  }

  return { start, stop, note, bindPagehide, state: snapshot };
}
